import { useGenericBi } from '../../../../lib/use-generic-bi';
import { useTranslation } from '../../../../lib/i18n';
import { SYMPHONY_COMMUNITY_URL } from '../shared';
import { CommunityIcon } from './icons';
import drawerStyles from '../mobile/MobileSidebar.module.css';
import styles from './CommunityLink.module.css';

/**
 * "Join our community" — the row at the TOP of the menu's floor, directly above the credits card
 * (Figma `Sidebar-Menu` 2679:3158: the bottom cluster is this row, a 12px gap, then the credits +
 * account block).
 *
 * ONE component for both web viewports, rendered by `WideSidebar` and by `MobileFooter`, because the
 * two only differ in skin — the wide rail's 14px `orion-nav-item` vs the drawer's 16px flat row. Two
 * copies of "a glyph, a label and an external href" is exactly the shape that drifts: one gains the
 * `rel`, the other keeps a stale URL.
 *
 * In the DRAWER it is deliberately a child of `.footer`, not of the scroll region — so the
 * conversations list scrolls away UNDER it through the footer's upward fade, the same floor the credits
 * card sits on. Putting it in the scroll region would let it scroll out of reach, which is the one
 * thing that fade exists to prevent.
 *
 * Unlike every other row here this is an EXTERNAL destination, so it is a plain `<a>` with
 * `target="_blank"` — not a next/link — and carries `rel="noopener noreferrer"`: `noopener` because a
 * `_blank` tab otherwise gets a live `window.opener` handle back into the app, and `noreferrer` so the
 * invite page is not told which Symphony URL the reader came from.
 *
 * Its native twins open the same URL in the system browser from the same place in the drawer's pinned
 * floor (iOS `DrawerOverlay.communityRow`, Android `Drawer.kt`).
 */
export function CommunityLink({
  variant,
  collapsed = false,
}: {
  /** `rail` = the wide (desktop) rail's 14px row; `drawer` = the narrow drawer's 16px flat row. */
  variant: 'rail' | 'drawer';
  /** Wide rail only: the collapsed icon strip hides the label and centers the glyph, like every nav row. */
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const label = t('sidebar.community.join');

  const common = {
    href: SYMPHONY_COMMUNITY_URL,
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: () => logPageClickBi({ page_name: 'menu', button_name: 'join_community', origin: 'menu' }),
  } as const;

  if (variant === 'drawer') {
    return (
      <a {...common} className={drawerStyles.row}>
        <span aria-hidden className={drawerStyles.rowIcon}>
          <CommunityIcon />
        </span>
        <span className={drawerStyles.rowLabel}>{label}</span>
      </a>
    );
  }

  // The wide rail. Same ramp and geometry as NavRail's `NavItem` — 14px Medium over a 16px glyph, not
  // the detached Figma layer's 12px/18px, which no other row in the rail uses.
  return (
    <a
      {...common}
      title={collapsed ? label : undefined}
      className={`orion-nav-item ${styles.railRow}${collapsed ? ` ${styles.railRowCollapsed}` : ''}`}
    >
      <span aria-hidden className={`orion-nav-icon ${styles.railIcon}`}>
        <CommunityIcon />
      </span>
      {/* The label is dropped in the collapsed strip and `orion-rail-label` eases the width to 0 on the
          rail's own curve — NavItem's exact pair, so the two rows collapse as one motion. */}
      {!collapsed && <span className="orion-rail-label">{label}</span>}
    </a>
  );
}
